// Autologin / handoff bootstrap — escape IG-webview → navegador real para conectar MP.
//
// Problema: los leads vienen de ads de IG → navegan en el webview de Instagram, donde
// MercadoPago discontinuó el OAuth y Apple no deja forzar Safari. La salida es abrir en
// el navegador real; pero ese navegador es una sesión limpia → caería en /login. Este
// módulo establece la sesión ANTES de montar React, a partir de la URL:
//
//  • ?ott=<token>  → token OPACO de un solo uso (producción): se canjea en cyclone
//                    (POST /sessions/handoff-redeem) por una sesión corta (app-JWT).
//  • ?otl=<token>  → token DIRECTO (solo testing): se siembra tal cual. Inerte en el
//                    build público salvo que el handoff lo active en runtime.
//
// El token queda en `flashPerfAccessToken` (lo reusa el perf-token path: Bearer hacia
// cyclone, contexto de auth y gate del router) y se marca `flashHandoffActive` para
// activar ese path también en el build público. Después se limpia la URL.

import {
  PERF_ACCESS_TOKEN_STORAGE_KEY,
  HANDOFF_ACTIVE_KEY,
  HANDOFF_LANDED_SOURCE_KEY,
  PERF_TOKEN_AUTH_ENABLED
} from '@/auth/perfTokenAuth';

const OTT_PARAM = 'ott';
const OTL_PARAM = 'otl';
const VENDOR_PARAM = 'vendor';
const SRC_PARAM = 'src';
const NEXT_PARAM = 'next';
// Adónde puede llevar un autologin además de `/`. Lista cerrada: un `next` libre sería
// una redirección abierta con la sesión ya puesta. `/setup` deriva al paso donde quedó;
// Robin abre un curso de Mis cursos (con la clase donde quedó) con su sesión, y las tareas
// del checklist que en el teléfono no tienen pantalla: importar clientes y las fichas; y
// Fichaje (`/equipo/asistencia`) desde el push de PIN bloqueado.
const ALLOWED_NEXT = [
  /^\/setup$/,
  /^\/mis-cursos\/\d+(?:\?classId=\d+)?$/,
  /^\/clientes\?importar=1$/,
  /^\/fichas$/,
  /^\/equipo\/asistencia$/
];
const isAllowedNext = (next: string): boolean => ALLOWED_NEXT.some((pattern) => pattern.test(next));
const GOD_MODE_STORAGE_KEY = 'vendorUsernameGodMode';

// Limpia los params del handoff y, opcionalmente, reemplaza el path. cyclone acuña
// el token contra `/autologin?ott=`, que NO es una ruta de Flash (daría 404 al montar
// React). Cuando establecemos la sesión mandamos a `/`, y el router de Flash resuelve
// el destino real (onboarding si falta setup, dashboard si ya está).
const stripParams = (params: URLSearchParams, path?: string): void => {
  params.delete(OTT_PARAM);
  params.delete(OTL_PARAM);
  params.delete(VENDOR_PARAM);
  params.delete(SRC_PARAM);
  params.delete(NEXT_PARAM);
  // Un `next` con query (`/mis-cursos/12?classId=34`) suma la suya a la que queda.
  const [basePath, nextQuery] = (path ?? window.location.pathname).split('?');
  new URLSearchParams(nextQuery).forEach((value, key) => params.set(key, value));
  const qs = params.toString();
  const cleanUrl = basePath + (qs ? `?${qs}` : '') + window.location.hash;
  window.history.replaceState(window.history.state, '', cleanUrl);
};

const activateSession = (token: string, vendor?: string): void => {
  window.localStorage.setItem(PERF_ACCESS_TOKEN_STORAGE_KEY, token);
  window.localStorage.setItem(HANDOFF_ACTIVE_KEY, '1');
  // useLocalStorage guarda JSON-serializado; god-mode se lee con JSON.parse. Solo se usa
  // en el path de testing (?otl + ?vendor); en producción el JWT canjeado ya es del vendor.
  if (vendor) window.localStorage.setItem(GOD_MODE_STORAGE_KEY, JSON.stringify(vendor));
};

export const initAutologin = async (): Promise<void> => {
  if (typeof window === 'undefined') return;

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  const ott = params.get(OTT_PARAM)?.trim();
  const otl = params.get(OTL_PARAM)?.trim();
  const vendor = params.get(VENDOR_PARAM)?.trim();
  const src = params.get(SRC_PARAM)?.trim();
  const next = params.get(NEXT_PARAM)?.trim();
  if (!ott && !otl) return;

  // One-shot: si el handoff trae `?src=` (ej. directorio lex_luthor), marcamos el
  // aterrizaje para que analytics emita `flash_handoff_landed` cuando PostHog cargue.
  const markLanded = (): void => {
    if (!src) return;
    try {
      window.sessionStorage.setItem(HANDOFF_LANDED_SOURCE_KEY, src.slice(0, 40));
    } catch {
      // sessionStorage puede fallar en modos restrictivos; el aterrizaje igual sucede.
    }
  };

  let sessionEstablished = false;

  try {
    if (ott) {
      // Canjear el token opaco por una sesión corta (single-use, lo borra el backend).
      const api = import.meta.env.VITE_CYCLONE_API_URL;
      const resp = await fetch(`${api}/sessions/handoff-redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: ott })
      });
      if (resp.ok) {
        const data = await resp.json();
        // Sin vendor/god-mode: el JWT canjeado YA está scopeado al vendor autenticado.
        // (Pasar ?vendor acá habilitaría god-mode por URL — agujero de privilegios.)
        if (data?.access_token) {
          activateSession(data.access_token);
          markLanded();
          sessionEstablished = true;
        }
      }
      // Si falla: no seteamos nada → el usuario cae en /login (link vencido/usado).
    } else if (otl && PERF_TOKEN_AUTH_ENABLED) {
      // ?otl = token crudo en la URL. SOLO para builds de test/perf; en el build
      // público se ignora (en prod la única vía es ?ott, opaco y canjeado).
      activateSession(otl, vendor);
      sessionEstablished = true;
    }
  } catch {
    // fetch/localStorage pueden fallar; seguimos y la app resolverá el estado de auth.
  } finally {
    // cyclone acuña el token contra `/autologin`, que no es una ruta de Flash. Si
    // establecimos sesión y estamos ahí, mandamos a `/` (o al `next` permitido) para no
    // montar React en un 404.
    const landing = next && isAllowedNext(next) ? next : '/';
    const redirectPath =
      sessionEstablished && window.location.pathname === '/autologin' ? landing : undefined;
    stripParams(params, redirectPath);
  }
};
